import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import WorkspaceHeader from './WorkspaceHeader ';
import Profile from '../Profile/Profile';
import './Sidebar.css';
import { Bell, Trash, X, } from 'lucide-react';
import ReactCardFlip from 'react-card-flip';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import StoreEmail from '../Mail/StoreEmail'
import AddChannelModal from '../AddChannelModal';
import Can from '../../../../utils/permissions/Can';

const getFreeAvatarUrl = (seed = 'user') =>
  `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`;

const Sidebar = ({
  workspaces,
  selectedWorkspaceId,
  setSelectedWorkspaceId,
  channels,
  isMailMode,
  setIsMailMode,
  directMessages,
  groups = [],
  activeChannel,
  sidebarCollapsed,
  onToggleCollapse,
  onChannelClick,
  currentUser,
  onStartDirectMessage,
  onUserStatusChange,
  onUserProfileUpdate,
  availableUsers = [],
  fetchChannels,


}) => {
  const getStatusClass = (status) => {
    const statusClasses = {
      online: 'status-online',
      offline: 'status-offline',
    };
    return statusClasses[status] || statusClasses.offline;
  };
  const { Post, Get, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [newName, setNewName] = useState('');
  const [workspaceList, setWorkspaceList] = useState(workspaces);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMemberInputFocused, setIsMemberInputFocused] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);



  const [profileData, setProfileData] = useState({
    name: currentUser.name,
    email: currentUser.email || '',
    phone: currentUser.phone || '',
    bio: currentUser.bio || '',
    location: currentUser.location || '',
    avatar: currentUser.avatar || null,
  });



  const selectedWorkspace = workspaceList.find(w => w.id === selectedWorkspaceId);
  const openModal = (type) => {
    setModalType(type);
    setNewName('');
    setSelectedMembers([]);
    setMemberSearchQuery('');
    setShowModal(true);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const [unreadCounts, setUnreadCounts] = useState({
    total: 0,
    direct: 0,
    channel: 0,
    notifications: []
  });

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!currentUser?.id) return;

      try {
        const apiUrl = `${getAPI("getUnreadMessages")}?message_to=${currentUser.id}`;
        const response = await Get(apiUrl);
        setUnreadCounts({
          total: response.total_unread_count || 0,
          direct: response.unread_direct_count || 0,
          channel: response.unread_channel_count || 0,
          notifications: [
            ...(response.unread_direct_messages || []),
            ...(response.unread_channel_messages || [])
          ]
        });
      } catch (error) {
        console.error('Error fetching unread messages:', error);

      }
    };

    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 30000);

    return () => clearInterval(interval);
  }, [currentUser?.id]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour ago`;
    return `${Math.floor(diffInSeconds / 86400)} day ago`;
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Channel name cannot be empty.");
      return;
    }

    if (!storeId) {
      console.error("Store ID is undefined. Cannot create channel.");
      toast.error("Store ID is missing.");
      return;
    }

    if (modalType === 'channel') {
      const memberIds = selectedMembers.map(member => member.id);
      const memberNames = selectedMembers.map(member => member.name);

      const payload = {
        store: storeId,
        title: newName.trim(),
        members: memberIds,
        members_name: memberNames,
      };

      try {
        const response = await Post('channel', payload);
        if (response.status === 201) {

          if (fetchChannels) {
            fetchChannels();
          }
          setNewName('');
          setSelectedMembers([]);
          setMemberSearchQuery('');
          setShowModal(false);
          toast.success('Channel created successfully!');
        } else {
          console.error("Error from API:", response);
          toast.error('Failed to create channel.');
        }
      } catch (error) {
        console.error('Error creating channel:', error);
        toast.error('An error occurred while creating the channel.');
      }
    }
  };
  const handleCloseModal = () => {
    setNewName('');
    setSelectedMembers([]);
    setMemberSearchQuery('');
    setShowModal(false);
  };

  const handleMemberSelect = (user) => {
    const isAlreadySelected = selectedMembers.find(member => member.id === user.id);
    if (!isAlreadySelected) {
      setSelectedMembers(prev => [...prev, user]);
    }
    setMemberSearchQuery('');
  };

  const handleMemberRemove = (userId) => {
    setSelectedMembers(prev => prev.filter(member => member.id !== userId));
  };
  const filteredUsers = availableUsers.filter(user =>
    user.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) &&
    !selectedMembers.find(selected => selected.id === user.id)
  );

  const getGroupStatus = (group) => {
    const hasOnlineMembers = group.members.some(member =>
      directMessages.find(dm => dm.id === member.id)?.status === 'online'
    );
    return hasOnlineMembers ? 'online' : 'offline';
  };

  const getAbbreviatedGroupName = (groupName) => {
    const names = groupName.split(', ');
    if (names.length <= 2) return groupName;
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  const handleStatusChange = (newStatus) => {
    if (onUserStatusChange) {
      onUserStatusChange(newStatus);
    }
  };

  const handleProfileUpdate = (profileData) => {
    if (onUserProfileUpdate) {
      onUserProfileUpdate(profileData);
    }
  };

  const handleNotificationClick = (e) => {
    e.stopPropagation();
    setShowNotifications(!showNotifications);
  };

  const handleNotificationItemClick = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setShowNotifications(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message': return '💬';
      case 'mention': return '🏷️';
      case 'system': return '⚙️';
      default: return '📢';
    }
  };

  return (
    <>
      <div className={`chat-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {!sidebarCollapsed && (
          <div className="app-switcher">
            <button
              onClick={() => setIsMailMode(false)}
              className={!isMailMode ? 'active' : ''}
            >
              App Chat
            </button>
            <button
              onClick={() => setIsMailMode(true)}
              className={isMailMode ? 'active' : ''}
            >
              Mail App
            </button>
          </div>
        )}

        <div key="front" className="sidebar-content-container">
          <ReactCardFlip isFlipped={isMailMode} flipDirection="horizontal">

            <div key="front" className="sidebar-content-wrapper">
              <WorkspaceHeader
                workspace={selectedWorkspace}
                sidebarCollapsed={sidebarCollapsed}
                onToggleCollapse={onToggleCollapse}
                isMailApp={isMailMode}
                setIsMailApp={setIsMailMode}
              />


              <div className="sidebar-content">
                <div className="sidebar-section">
                  {!sidebarCollapsed && (
                    <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 className="section-title">Workspaces</h3>
                      <button
                        className="add-btn"
                        title="Create Workspace"

                      >
                        +
                      </button>
                    </div>
                  )}
                  <div className="sidebar-items">
                    {workspaceList.map(workspace => (
                      <div
                        key={workspace.id}
                        onClick={() => setSelectedWorkspaceId(workspace.id)}
                        className={`sidebar-item ${workspace.id === selectedWorkspaceId ? 'active' : ''}`}
                        title={sidebarCollapsed ? workspace.name : ''}
                      >
                         <div className="item-content">
                          <span className="workspace-icon">🏢</span>
                          {!sidebarCollapsed && (
                            <span className="item-name">{workspace.name}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sidebar-section">
                  <div className="section-header">
                    {!sidebarCollapsed && (
                      <>
                        <h3 className="section-title">Channels</h3>
                        <Can module="chatService" action="add">
                          <button
                            className="add-btn"
                            title="Create Channel"
                            onClick={() => openModal('channel')}
                          >
                            +
                          </button>
                        </Can>
                      </>
                    )}
                  </div>
                  <div className="sidebar-items">
                    {channels.map(channel => (
                      <div
                        key={channel.id}
                        onClick={() => onChannelClick(channel.id, channel.name)}
                        className={`sidebar-item ${activeChannel === channel.id ? 'active' : ''}`}
                        title={sidebarCollapsed ? channel?.name || 'Unnamed Channel' : ''}
                      >
                       <div className="item-content">
                          <span className="channel-icon">#</span>
                          {!sidebarCollapsed && (
                            <span className="item-name">
                              {channel?.name ? channel.name.replace('#', '') : 'Unnamed Channel'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                  </div>
                </div>

                {groups.length > 0 && (
                  <div className="sidebar-section">
                    <div className="section-header">
                      {!sidebarCollapsed && (
                        <h3 className="section-title">Groups</h3>
                      )}
                    </div>
                    <div className="sidebar-items">
                      {groups.map(group => (
                        <div
                          key={group.id}
                          onClick={() => onChannelClick(group.id, group.name)}
                          className={`sidebar-item ${activeChannel === group.id ? 'active' : ''}`}
                          title={sidebarCollapsed ? `${group.name} (${group.members.length} members)` : ''}
                        >
                          <div className="item-content">
                            <div className="user-ava">
                              <div className="ava-circle group-ava">👥</div>
                            </div>
                            {!sidebarCollapsed && (
                              <div className="group-info">
                                <span className="item-name">
                                  {sidebarCollapsed ? getAbbreviatedGroupName(group.name) : group.name}
                                </span>
                                <span className="group-member-count">
                                  {group.members.length} members
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="sidebar-section">
                  <div className="section-header">
                    {!sidebarCollapsed && (
                      <>
                        <h3 className="section-title">Direct Messages</h3>
                        <button
                          className="add-btn"
                          title="Start direct message"
                          onClick={onStartDirectMessage}
                        >
                          +
                        </button>
                      </>
                    )}
                  </div>
                  <div className="sidebar-items">
                    {directMessages.map(dm => (
                      <div
                        key={dm.id}
                        onClick={() => onChannelClick(dm.id, dm.name)}
                        className={`sidebar-item ${activeChannel === dm.id ? 'active' : ''}`}
                        title={sidebarCollapsed ? `${dm.name} (${dm.status})` : ''}
                      >
                          <div className="item-content">
                            <div className="user-ava">
                            <div className="ava-circle">
                              <img className="chat-avatar-img" src={getFreeAvatarUrl(dm.name)} alt="" />
                            </div>
                            <div className={`status-indicator ${getStatusClass(dm.status)}`}></div>
                          </div>
                          {!sidebarCollapsed && <span className="item-name">{dm.name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div key="back" className="sidebar-content-wrapper">
              <StoreEmail/>
            </div>
          </ReactCardFlip>
        </div>

        <div className="user-pro">
          <div className="profile-content" onClick={() => setShowProfileModal(true)}>
            <div className="profile-ava">
              <div className="ava-circle">
                <img className="chat-avatar-img" src={getFreeAvatarUrl(currentUser.name)} alt="" />
              </div>
              <div className={`status-indicator ${getStatusClass(currentUser.status)}`}></div>
            </div>
            {!sidebarCollapsed && (
              <div className="profile-info">
                <div className="profile-top">
                  <p className="profile-name">{currentUser.name}</p>
                  <div className="profile-actions">
                    <button
                      className="notification-btn"
                      onClick={handleNotificationClick}
                      title="Notifications"
                    >
                      <Bell size={24} />
                      {unreadCounts.total > 0 && (
                        <span className="notification-badge">{unreadCounts.total}</span>
                      )}
                    </button>
                  </div>
                </div>
                <p className="profile-status">{currentUser.status}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showNotifications && !sidebarCollapsed && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h4>Notifications</h4>
            <div className="notifications-actions">

              { }
              <button
                className="clear-all-btn"
                onClick={clearAllNotifications}
                title="Clear all"
              >
                <Trash size={16} />
              </button>
              <button
                className="close-notifications-btn"
                onClick={() => setShowNotifications(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="notifications-body">
            {unreadCounts.notifications.length === 0 ? (
              <div className="empty-notifications">
                <Bell size={32} className="empty-icon" />
                <p>No notifications</p>
                <span>You're all caught up!</span>
              </div>
            ) : (
              <div className="notifications-list">
                {unreadCounts.notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                    onClick={() => handleNotificationItemClick(notification.id)}
                  >
                    <div className="notification-avatar">
                      {notification.avatar ? (
                        <img src={notification.avatar} alt="" className="notification-avatar-img" />
                      ) : (
                        <div className="notification-icon">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{notification.time}</div>
                    </div>
                    {!notification.read && <div className="unread-indicator"></div>}
                  </div>
                ))}
              </div>

            )}
          </div>
        </div>
      )}

      {showProfileModal && (
        <Profile
          currentUser={currentUser}
          onStatusChange={handleStatusChange}
          onProfileUpdate={(data) => {
            handleProfileUpdate(data);
            setProfileData(data);
          }}
          onClose={() => setShowProfileModal(false)}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          initialProfileData={profileData}
        />
      )}
<AddChannelModal
  showModal={showModal}
  handleCloseModal={handleCloseModal}
  newName={newName}
  setNewName={setNewName}
  selectedMembers={selectedMembers}
  handleMemberRemove={handleMemberRemove}
  memberSearchQuery={memberSearchQuery}
  setMemberSearchQuery={setMemberSearchQuery}
  isMemberInputFocused={isMemberInputFocused}
  setIsMemberInputFocused={setIsMemberInputFocused}
  availableUsers={availableUsers}
  filteredUsers={filteredUsers}
  handleMemberSelect={handleMemberSelect}
  handleCreate={handleCreate}
/>

    </>
  );
};

export default Sidebar;
